import React, { useState, useEffect } from 'react';
import { 
  FileCheck2, 
  ShieldCheck, 
  ShieldAlert,
  Key, 
  Upload, 
  Lock, 
  Building2, 
  AlertTriangle, 
  CheckCircle2, 
  Power, 
  HelpCircle, 
  RefreshCw, 
  X, 
  Eye, 
  EyeOff,
  Check,
  Calendar,
  Search,
  Image as ImageIcon,
  FileText,
  Zap,
  Copy,
  Download,
  Printer
} from 'lucide-react';
import { FiscalService, FiscalSettingsData, FiscalCertificateStatus } from '../../../services/fiscalService';
import { fetchAddressByCep, fetchCompanyByCnpj } from '../services/publicDataServices';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { getSefazDiagnosis } from '../utils/sefazStatusDictionary';

interface FiscalConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultLogoUrl?: string;
}

export const FiscalConfigModal: React.FC<FiscalConfigModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultLogoUrl,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [toggling, setToggling] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Configurações atuais carregadas do backend
  const [currentSettings, setCurrentSettings] = useState<FiscalSettingsData | null>(null);
  const [certStatus, setCertStatus] = useState<FiscalCertificateStatus | null>(null);

  // Campos do Formulário
  const [tenantCnpj, setTenantCnpj] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [inscricaoEstadual, setInscricaoEstadual] = useState('');
  const [inscricaoMunicipal, setInscricaoMunicipal] = useState('');
  const [crt, setCrt] = useState<number>(1); // 1 = Simples Nacional
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [codigoMunicipio, setCodigoMunicipio] = useState('3550308'); // Padrão SP
  const [municipio, setMunicipio] = useState('São Paulo');
  const [uf, setUf] = useState('SP');
  const [cep, setCep] = useState('');
  const [ambiente, setAmbiente] = useState<number>(2); // 2 = Homologação, 1 = Produção
  const [cscTokenId, setCscTokenId] = useState('000001');
  const [cscTokenValue, setCscTokenValue] = useState('');
  const [certificatePassword, setCertificatePassword] = useState('');
  const [pfxBase64, setPfxBase64] = useState<string | null>(null);
  const [pfxFileName, setPfxFileName] = useState<string | null>(null);
  const [nfceEnabled, setNfceEnabled] = useState<boolean>(true);
  const [nfeModel55Enabled, setNfeModel55Enabled] = useState<boolean>(false);
  const [nfseEnabled, setNfseEnabled] = useState<boolean>(false);
  const [seriePadraoNfce, setSeriePadraoNfce] = useState<number>(1);
  const [seriePadraoNfe55, setSeriePadraoNfe55] = useState<number>(1);
  const [seriePadraoNfse, setSeriePadraoNfse] = useState<number>(1);
  const [danfeLayoutNfe55, setDanfeLayoutNfe55] = useState<'retrato' | 'paisagem'>('retrato');
  const [logoUrl, setLogoUrl] = useState<string>('');

  const [showCscValue, setShowCscValue] = useState<boolean>(false);
  const [showCertPassword, setShowCertPassword] = useState<boolean>(false);

  // Estados de consulta automática BrasilAPI / Receita Federal
  const [searchingCep, setSearchingCep] = useState<boolean>(false);
  const [searchingCnpj, setSearchingCnpj] = useState<boolean>(false);
  const [autoFillNotice, setAutoFillNotice] = useState<string | null>(null);

  // Navegação por Abas (Artigo 3 - Material Design 3)
  const [activeTab, setActiveTab] = useState<'settings' | 'documents'>('settings');

  // Teste de Comunicação com SEFAZ
  const [testingSefaz, setTestingSefaz] = useState<boolean>(false);
  const [sefazStatusResult, setSefazStatusResult] = useState<{
    online: boolean;
    cStat?: string;
    motivo?: string;
    ambiente?: number;
    uf?: string;
    dhRecbto?: string;
    error?: string;
  } | null>(null);

  // Lista de Documentos Emitidos
  const [emittedDocuments, setEmittedDocuments] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [retryingDocId, setRetryingDocId] = useState<string | null>(null);

  const handleRetryDocument = async (docId: string) => {
    setRetryingDocId(docId);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const res = await FiscalService.retryRejectedDocument(docId);
      setSuccessMsg(res.message || 'Nota fiscal reprocessada e transmitida com sucesso!');
      await loadEmittedDocuments();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || 'Falha ao reemitir nota fiscal rejeitada.');
    } finally {
      setRetryingDocId(null);
    }
  };

  const handleTestSefaz = async () => {
    setTestingSefaz(true);
    setSefazStatusResult(null);
    try {
      const res = await FiscalService.checkSefazStatus();
      setSefazStatusResult(res);
    } catch (err: any) {
      setSefazStatusResult({
        online: false,
        error: err.message || 'Falha ao conectar com o serviço SEFAZ',
      });
    } finally {
      setTestingSefaz(false);
    }
  };

  const loadEmittedDocuments = async () => {
    setLoadingDocs(true);
    try {
      const res = await FiscalService.listDocuments(50, 0);
      if (res?.data) {
        setEmittedDocuments(res.data);
      }
    } catch (err) {
      console.warn('Erro ao carregar documentos fiscais:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleCopyKey = (key: string) => {
    if (!key) return;
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleDownloadXml = async (docId: string, filename?: string) => {
    setDownloadingDocId(docId);
    try {
      await FiscalService.downloadDocumentXml(docId, filename);
    } catch (err: any) {
      alert(`Erro ao baixar XML: ${err.message}`);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleDownloadPdf = async (docId: string, filename?: string) => {
    setDownloadingDocId(docId);
    try {
      await FiscalService.downloadDocumentPdf(docId, filename);
    } catch (err: any) {
      alert(`Erro ao baixar DANFE: ${err.message}`);
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Carrega configurações existentes
  useEffect(() => {
    if (isOpen) {
      loadSettings();
      loadEmittedDocuments();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const [data, cert] = await Promise.all([
        FiscalService.getSettings(),
        FiscalService.getCertificateStatus(),
      ]);

      if (cert) {
        setCertStatus(cert);
      }

      if (data) {
        setCurrentSettings(data);
        setTenantCnpj(data.tenant_cnpj || '');
        setRazaoSocial(data.razao_social || '');
        setNomeFantasia(data.nome_fantasia || '');
        setInscricaoEstadual(data.inscricao_estadual || '');
        setInscricaoMunicipal(data.inscricao_municipal || '');
        setCrt(data.crt || 1);
        setLogradouro(data.logradouro || '');
        setNumero(data.numero || '');
        setComplemento(data.complemento || '');
        setBairro(data.bairro || '');
        const rawIbge = (data.codigo_municipio || '').replace(/\D/g, '').slice(0, 7);
        setCodigoMunicipio(rawIbge || '3550308');
        setMunicipio(data.municipio || 'São Paulo');
        setUf(data.uf || 'SP');
        const rawCep = (data.cep || '').replace(/\D/g, '');
        setCep(rawCep.length === 8 ? `${rawCep.slice(0, 5)}-${rawCep.slice(5)}` : (data.cep || ''));
        setAmbiente(data.ambiente || 2);
        setCscTokenId(data.csc_token_id || '000001');
        setNfceEnabled(data.nfce_enabled !== false);
        setNfeModel55Enabled(Boolean(data.nfe_model55_enabled));
        setNfseEnabled(Boolean(data.nfse_enabled));
        setSeriePadraoNfce(data.serie_padrao || 1);
        setSeriePadraoNfe55(data.serie_padrao_nfe55 || 1);
        setSeriePadraoNfse(data.serie_padrao_nfse || 1);
        setDanfeLayoutNfe55(data.danfe_layout_nfe55 || 'retrato');
        setLogoUrl(data.logo_url || defaultLogoUrl || '');
      }
    } catch (err: any) {
      console.log('Nenhuma configuração fiscal prévia encontrada.');
    } finally {
      setLoading(false);
    }
  };

  // Alterna chave liga/desliga rápida
  const handleToggleFiscal = async () => {
    if (!currentSettings && !tenantCnpj) {
      setErrorMsg('Configure os dados fiscais e o certificado antes de ativar a emissão.');
      return;
    }

    setToggling(true);
    setErrorMsg(null);
    try {
      const newStatus = !nfceEnabled;
      const res = await FiscalService.toggleFiscal(newStatus);
      setNfceEnabled(res.nfce_enabled);
      setSuccessMsg(res.message);
      if (currentSettings) {
        setCurrentSettings({ ...currentSettings, nfce_enabled: res.nfce_enabled });
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao alternar chave fiscal.');
    } finally {
      setToggling(false);
    }
  };

  // Leitura do arquivo .pfx para base64
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPfxFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove cabeçalho data:...;base64, se existir
      const base64Clean = result.includes(',') ? result.split(',')[1] : result;
      setPfxBase64(base64Clean);
    };
    reader.readAsDataURL(file);
  };

  // Consulta endereço e código IBGE automático via CEP (BrasilAPI com fallback ViaCEP)
  const handleLookupCep = async (cepToLookup: string) => {
    const clean = cepToLookup.replace(/\D/g, '');
    if (clean.length !== 8) return;

    setSearchingCep(true);
    setAutoFillNotice(null);
    try {
      const result = await fetchAddressByCep(clean);
      if (result.success) {
        if (result.street) setLogradouro(result.street);
        if (result.neighborhood) setBairro(result.neighborhood);
        if (result.city) setMunicipio(result.city);
        if (result.state) setUf(result.state.toUpperCase());
        if (result.codigoIbge && result.codigoIbge.length === 7) {
          setCodigoMunicipio(result.codigoIbge);
          setAutoFillNotice(`Endereço e Cód. IBGE (${result.codigoIbge}) preenchidos automaticamente via BrasilAPI.`);
        } else {
          setAutoFillNotice('Endereço preenchido via consulta de CEP.');
        }
      }
    } catch {
      // Falha silenciosa para permitir digitação manual sem travar o operador
    } finally {
      setSearchingCep(false);
    }
  };

  // Consulta dados cadastrais na Receita Federal via BrasilAPI
  const handleLookupCnpj = async (cnpjToLookup?: string) => {
    const clean = (cnpjToLookup || tenantCnpj).replace(/\D/g, '');
    if (clean.length !== 14) {
      setErrorMsg('Digite os 14 dígitos numéricos do CNPJ para buscar os dados cadastrais.');
      return;
    }

    setSearchingCnpj(true);
    setErrorMsg(null);
    setAutoFillNotice(null);
    try {
      const result = await fetchCompanyByCnpj(clean);
      if (result.success) {
        if (result.cnpj) setTenantCnpj(result.cnpj);
        if (result.razaoSocial) setRazaoSocial(result.razaoSocial);
        if (result.nomeFantasia) setNomeFantasia(result.nomeFantasia);
        if (result.street) setLogradouro(result.street);
        if (result.number && result.number !== 'S/N') setNumero(result.number);
        if (result.complement) setComplemento(result.complement);
        if (result.neighborhood) setBairro(result.neighborhood);
        if (result.city) setMunicipio(result.city);
        if (result.state) setUf(result.state.toUpperCase());
        if (result.cep) {
          const raw = result.cep.replace(/\D/g, '');
          const formattedCep = raw.length === 8 ? `${raw.slice(0, 5)}-${raw.slice(5)}` : result.cep;
          setCep(formattedCep);
        }

        if (result.codigoIbge && result.codigoIbge.length === 7) {
          setCodigoMunicipio(result.codigoIbge);
          setAutoFillNotice(`Dados cadastrais e Cód. IBGE (${result.codigoIbge}) carregados via Receita Federal/BrasilAPI.`);
        } else if (result.cep) {
          const addr = await fetchAddressByCep(result.cep);
          if (addr.success && addr.codigoIbge && addr.codigoIbge.length === 7) {
            setCodigoMunicipio(addr.codigoIbge);
            setAutoFillNotice(`Dados da empresa e Cód. IBGE (${addr.codigoIbge}) carregados com sucesso.`);
          } else {
            setAutoFillNotice('Dados da empresa carregados via Receita Federal/BrasilAPI.');
          }
        } else {
          setAutoFillNotice('Dados da empresa carregados via Receita Federal/BrasilAPI.');
        }
      } else {
        setErrorMsg(result.error || 'Não foi possível encontrar a empresa na Receita Federal.');
      }
    } catch {
      setErrorMsg('Falha na consulta do CNPJ na BrasilAPI.');
    } finally {
      setSearchingCnpj(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    const cleanCnpj = (tenantCnpj || '').replace(/\D/g, '');
    if (!cleanCnpj || cleanCnpj.length !== 14) {
      setErrorMsg('CNPJ inválido. Digite os 14 dígitos numéricos do CNPJ da empresa.');
      return;
    }

    const cleanIE = (inscricaoEstadual || '').replace(/\D/g, '');
    if (!cleanIE) {
      setErrorMsg('Informe a Inscrição Estadual (IE) da empresa.');
      return;
    }

    if (!razaoSocial.trim()) {
      setErrorMsg('Informe a Razão Social (Nome Oficial) da empresa.');
      return;
    }

    const cleanIbge = (codigoMunicipio || '').replace(/\D/g, '');
    if (!cleanIbge || cleanIbge.length !== 7) {
      if (cleanIbge.length === 8) {
        setErrorMsg(
          `O Código IBGE digitado (${codigoMunicipio}) possui 8 dígitos e parece ser um CEP. O Código IBGE oficial do município possui exatamente 7 dígitos (ex: 3538709 para Piracicaba/SP ou 3550308 para São Paulo/SP).`
        );
      } else {
        setErrorMsg(
          'O Código IBGE do município é obrigatório e deve conter exatamente 7 dígitos numéricos (ex: 3538709 para Piracicaba/SP ou 3550308 para São Paulo/SP).'
        );
      }
      return;
    }

    if (nfceEnabled && (!cscTokenId || !cscTokenValue)) {
      setErrorMsg('Para emitir NFC-e (varejo), informe o Identificador (IdCSC) e o Token CSC fornecidos pela contabilidade/SEFAZ.');
      return;
    }

    if (!currentSettings?.certificate_valid_until && (!pfxBase64 || !certificatePassword)) {
      setErrorMsg('Selecione o arquivo do Certificado Digital (.pfx) e digite a senha.');
      return;
    }

    setSaving(true);
    try {
      await FiscalService.saveSettings({
        tenantCnpj: cleanCnpj,
        razaoSocial: razaoSocial.trim(),
        nomeFantasia: (nomeFantasia || razaoSocial).trim(),
        inscricaoEstadual: cleanIE,
        inscricaoMunicipal: inscricaoMunicipal.trim() || undefined,
        crt,
        logradouro: logradouro.trim(),
        numero: numero.trim() || 'S/N',
        complemento: complemento.trim(),
        bairro: bairro.trim(),
        codigoMunicipio: cleanIbge,
        municipio: municipio.trim(),
        uf: (uf || 'SP').trim().toUpperCase(),
        cep: cep.replace(/\D/g, ''),
        ambiente,
        cscTokenId: cscTokenId.trim(),
        cscTokenValue: cscTokenValue.trim(),
        certificatePfxBase64: pfxBase64 || undefined,
        certificatePassword: certificatePassword || undefined,
        seriePadrao: Number(seriePadraoNfce) || 1,
        seriePadraoNfce: Number(seriePadraoNfce) || 1,
        seriePadraoNfe55: Number(seriePadraoNfe55) || 1,
        seriePadraoNfse: Number(seriePadraoNfse) || 1,
        danfeLayoutNfe55,
        logoUrl: logoUrl.trim() || undefined,
        nfceEnabled,
        nfeModel55Enabled,
        nfseEnabled,
      });

      setSuccessMsg('Configurações fiscais salvas e validadas com sucesso!');
      await loadSettings();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || 'Falha ao salvar configurações fiscais.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white border border-zinc-200 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden my-auto flex flex-col max-h-[92vh]">
        
        {/* Cabeçalho do Modal */}
        <div className="p-4 sm:p-5 bg-zinc-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <FileCheck2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black tracking-tight">Emissão Fiscal & Tríade Tributária</h2>
              <p className="text-xs text-zinc-400">NFC-e (Mod. 65), NF-e (Mod. 55) e NFS-e Nacional sem intermediários pagos</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Barra da Chave Mestra Liga/Desliga */}
        <div className={`p-3 sm:px-6 flex items-center justify-between border-b transition-colors ${
          nfceEnabled 
            ? 'bg-emerald-50/80 border-emerald-200' 
            : 'bg-zinc-100 border-zinc-200'
        }`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
              nfceEnabled ? 'bg-emerald-600 text-white' : 'bg-zinc-400 text-white'
            }`}>
              <Power className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-zinc-900 flex items-center gap-1.5">
                Chave da Emissão Fiscal no Caixa:
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                  nfceEnabled ? 'bg-emerald-600 text-white' : 'bg-zinc-500 text-white'
                }`}>
                  {nfceEnabled ? 'LIGADA (Fiscal Ativo)' : 'DESLIGADA (Apenas Comprovante)'}
                </span>
              </div>
              <p className="text-[11px] text-zinc-600">
                {nfceEnabled 
                  ? 'Vendas no PDV geram NFC-e oficial com QR Code para o consumidor.'
                  : 'Vendas no PDV imprimem comprovante de venda não-fiscal, sem comunicar com a SEFAZ.'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleToggleFiscal}
            disabled={toggling}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              nfceEnabled ? 'bg-emerald-600' : 'bg-zinc-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                nfceEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Barra de Navegação por Abas (Material Design 3) */}
        <div className="flex border-b border-zinc-200 px-4 sm:px-6 bg-zinc-50/50">
          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            className={`py-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'settings'
                ? 'border-zinc-900 text-zinc-900 bg-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-800'
            }`}
          >
            <ShieldCheck className="w-4 h-4 text-zinc-600" />
            <span>Configurações & Certificado A1</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('documents');
              loadEmittedDocuments();
            }}
            className={`py-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'documents'
                ? 'border-zinc-900 text-zinc-900 bg-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-800'
            }`}
          >
            <FileText className="w-4 h-4 text-zinc-600" />
            <span>Notas Fiscais Emitidas na SEFAZ</span>
            {emittedDocuments.length > 0 && (
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold bg-zinc-200 text-zinc-800">
                {emittedDocuments.length}
              </span>
            )}
          </button>
        </div>

        {/* Corpo do Formulário com Scroll */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-5">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-3 text-zinc-500">
              <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
              <p className="text-xs font-bold">Carregando dados fiscais...</p>
            </div>
          ) : activeTab === 'settings' ? (
            <form onSubmit={handleSave} className="space-y-5">
              {/* Feedback de Mensagens */}
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2.5 text-red-800 text-xs">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              {successMsg && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-emerald-800 text-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{successMsg}</span>
                </div>
              )}
              {autoFillNotice && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between gap-2.5 text-blue-900 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>{autoFillNotice}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAutoFillNotice(null)}
                    className="text-blue-500 hover:text-blue-800 p-1 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Card de Status do Certificado com Diagnóstico de Validade */}
              {certStatus ? (
                <div className={`p-3.5 rounded-xl border flex items-center justify-between text-xs transition-colors ${
                  certStatus.warningLevel === 'expired'
                    ? 'bg-red-50 border-red-300 text-red-900'
                    : certStatus.warningLevel === 'critical_7d'
                    ? 'bg-amber-100/90 border-amber-400 text-amber-950 ring-1 ring-amber-400'
                    : certStatus.warningLevel === 'warning_30d'
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-emerald-50/80 border-emerald-200 text-emerald-900'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      certStatus.warningLevel === 'expired'
                        ? 'bg-red-200 text-red-800'
                        : certStatus.warningLevel === 'critical_7d'
                        ? 'bg-amber-200 text-amber-900'
                        : certStatus.warningLevel === 'warning_30d'
                        ? 'bg-amber-200 text-amber-800'
                        : 'bg-emerald-200 text-emerald-800'
                    }`}>
                      {certStatus.warningLevel === 'expired' || certStatus.warningLevel === 'critical_7d' ? (
                        <ShieldAlert className="w-5 h-5" />
                      ) : (
                        <ShieldCheck className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <div className="font-bold flex items-center gap-2">
                        <span>Certificado Digital e-CNPJ A1</span>
                        {certStatus.warningLevel === 'expired' && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-red-600 text-white">
                            Vencido
                          </span>
                        )}
                        {certStatus.warningLevel === 'critical_7d' && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-amber-600 text-white animate-pulse">
                            Expira em {certStatus.daysRemaining}d
                          </span>
                        )}
                        {certStatus.warningLevel === 'warning_30d' && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-amber-500 text-white">
                            Atenção
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] opacity-80 flex items-center gap-1.5 mt-0.5">
                        <Calendar className="w-3.5 h-3.5 shrink-0" />
                        {certStatus.warningLevel === 'expired'
                          ? `Expirou em ${new Date(certStatus.validUntil).toLocaleDateString('pt-BR')}. Emissões bloqueadas pela SEFAZ.`
                          : `Válido até ${new Date(certStatus.validUntil).toLocaleDateString('pt-BR')} (${certStatus.daysRemaining} dias restantes)`}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full shrink-0 ${
                    ambiente === 1 
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                      : 'bg-amber-100 text-amber-800 border border-amber-300'
                  }`}>
                    {ambiente === 1 ? 'Produção' : 'Homologação'}
                  </span>
                </div>
              ) : currentSettings?.certificate_valid_until ? (
                <div className="p-3.5 bg-zinc-50 border border-zinc-200 rounded-xl flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-zinc-900">Certificado Digital e-CNPJ A1 Ativo</div>
                      <div className="text-zinc-500 text-[11px] flex items-center gap-1.5 mt-0.5">
                        <Calendar className="w-3.5 h-3.5 text-zinc-400" />
                        Válido até: {new Date(currentSettings.certificate_valid_until).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full ${
                    ambiente === 1 
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                      : 'bg-amber-100 text-amber-800 border border-amber-300'
                  }`}>
                    {ambiente === 1 ? 'Produção' : 'Homologação'}
                  </span>
                </div>
              ) : null}

              {/* Card de Teste de Conexão com SEFAZ */}
              {(certStatus?.isValid || currentSettings?.certificate_valid_until) && (
                <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-bold text-zinc-800">
                        Diagnóstico de Conexão com a SEFAZ-{uf || 'SP'}:
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleTestSefaz}
                      disabled={testingSefaz}
                      className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-colors shadow-2xs"
                    >
                      <Zap className="w-3.5 h-3.5 text-amber-400" />
                      <span>{testingSefaz ? 'Consultando SEFAZ...' : 'Testar Conexão SEFAZ'}</span>
                    </button>
                  </div>

                  {sefazStatusResult && (
                    <div className={`p-2.5 rounded-lg text-xs font-medium border flex items-center gap-2 ${
                      sefazStatusResult.online
                        ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                        : 'bg-rose-50 text-rose-900 border-rose-300'
                    }`}>
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${sefazStatusResult.online ? 'bg-emerald-600' : 'bg-rose-600'}`} />
                      <div className="min-w-0">
                        <div className="font-bold">
                          {sefazStatusResult.online
                            ? `SEFAZ-${sefazStatusResult.uf || 'SP'} em Operação (cStat ${sefazStatusResult.cStat}: ${sefazStatusResult.motivo})`
                            : `Falha na Comunicação SEFAZ: ${sefazStatusResult.error || sefazStatusResult.motivo || 'Serviço Indisponível'}`}
                        </div>
                        {sefazStatusResult.dhRecbto && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            Resposta recebida em: {new Date(sefazStatusResult.dhRecbto).toLocaleString('pt-BR')} (Ambiente {sefazStatusResult.ambiente === 1 ? 'Produção' : 'Homologação'})
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SEÇÃO 1: DADOS DA EMPRESA */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-800 border-b pb-1.5">
                  <Building2 className="w-4 h-4 text-zinc-500" /> 1. Dados do Emitente
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block font-bold text-zinc-700">CNPJ da Empresa:</label>
                      <button
                        type="button"
                        onClick={() => handleLookupCnpj()}
                        disabled={searchingCnpj}
                        className="text-[10px] font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1 cursor-pointer disabled:opacity-50"
                      >
                        {searchingCnpj ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            <span>Buscando...</span>
                          </>
                        ) : (
                          <>
                            <Search className="w-3 h-3" />
                            <span>Buscar Dados (BrasilAPI)</span>
                          </>
                        )}
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="00.000.000/0000-00"
                      value={tenantCnpj}
                      onChange={e => {
                        const val = e.target.value;
                        setTenantCnpj(val);
                        const digits = val.replace(/\D/g, '');
                        if (digits.length === 14 && !razaoSocial) {
                          handleLookupCnpj(digits);
                        }
                      }}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 font-mono text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">Inscrição Estadual (IE):</label>
                    <input
                      type="text"
                      placeholder="123456789111"
                      value={inscricaoEstadual}
                      onChange={e => setInscricaoEstadual(e.target.value)}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 font-mono text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block font-bold text-zinc-700">Inscrição Municipal (IM):</label>
                      <span className="text-[10px] text-zinc-400">Para NFS-e</span>
                    </div>
                    <input
                      type="text"
                      placeholder="1234567 (Opcional)"
                      value={inscricaoMunicipal}
                      onChange={e => setInscricaoMunicipal(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 font-mono text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block font-bold text-zinc-700 mb-1">Razão Social (Nome Oficial):</label>
                    <input
                      type="text"
                      placeholder="EMPRESA EXEMPLO LTDA"
                      value={razaoSocial}
                      onChange={e => setRazaoSocial(e.target.value)}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 text-xs focus:bg-white focus:outline-none focus:border-amber-500 uppercase"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">Nome Fantasia (Opcional):</label>
                    <input
                      type="text"
                      placeholder="MEU COMÉRCIO"
                      value={nomeFantasia}
                      onChange={e => setNomeFantasia(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 text-xs focus:bg-white focus:outline-none focus:border-amber-500 uppercase"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">Regime Tributário (CRT):</label>
                    <select
                      value={crt}
                      onChange={e => setCrt(Number(e.target.value))}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                    >
                      <option value={1}>1 - Simples Nacional (Padrão)</option>
                      <option value={2}>2 - Simples Nacional - Excesso Sublimite</option>
                      <option value={3}>3 - Regime Normal (Lucro Presumido/Real)</option>
                    </select>
                  </div>
                </div>

                {/* Endereço do Estabelecimento */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs pt-1">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[11px] font-bold text-zinc-600">CEP:</label>
                      {searchingCep && (
                        <span className="text-[10px] text-amber-600 font-bold flex items-center gap-1">
                          <RefreshCw className="w-2.5 h-2.5 animate-spin" /> Buscando...
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="00000-000"
                      value={cep}
                      maxLength={9}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                        if (digits.length > 5) {
                          setCep(`${digits.slice(0, 5)}-${digits.slice(5)}`);
                        } else {
                          setCep(digits);
                        }
                        if (digits.length === 8) {
                          handleLookupCep(digits);
                        }
                      }}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 font-mono focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-[11px] font-bold text-zinc-600 mb-1">Logradouro / Rua:</label>
                    <input
                      type="text"
                      placeholder="Avenida Paulista"
                      value={logradouro}
                      onChange={e => setLogradouro(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-zinc-600 mb-1">Número:</label>
                    <input
                      type="text"
                      placeholder="1000"
                      value={numero}
                      onChange={e => setNumero(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-zinc-600 mb-1">Complemento:</label>
                    <input
                      type="text"
                      placeholder="Sala 1, Bloco B (Opcional)"
                      value={complemento}
                      onChange={e => setComplemento(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-zinc-600 mb-1">Bairro:</label>
                    <input
                      type="text"
                      placeholder="Centro"
                      value={bairro}
                      onChange={e => setBairro(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-zinc-600 mb-1">Município:</label>
                    <input
                      type="text"
                      placeholder="São Paulo"
                      value={municipio}
                      onChange={e => setMunicipio(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-zinc-600 mb-1">UF:</label>
                    <input
                      type="text"
                      maxLength={2}
                      value={uf}
                      onChange={e => setUf(e.target.value.toUpperCase())}
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2 text-xs text-zinc-900 text-center font-bold focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-4 bg-amber-50/80 border border-amber-200 rounded-xl p-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <div className="flex-1">
                        <label className="block text-[11px] font-bold text-amber-950 mb-0.5">
                          Cód. IBGE Município (7 dígitos numéricos):
                        </label>
                        <p className="text-[10px] text-amber-900/80 leading-relaxed">
                          Código de 7 dígitos da cidade para a SEFAZ (ex: Piracicaba = <strong className="font-mono text-zinc-900">3538709</strong>, São Paulo = <strong className="font-mono text-zinc-900">3550308</strong>). <strong className="text-red-700">Não informe o CEP neste campo.</strong>
                        </p>
                      </div>
                      <div className="w-full sm:w-44 shrink-0">
                        <input
                          type="text"
                          placeholder="3538709"
                          maxLength={7}
                          value={codigoMunicipio}
                          onChange={e => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 7);
                            setCodigoMunicipio(digits);
                          }}
                          className="w-full bg-white border border-amber-300 rounded-lg p-2 text-xs text-zinc-900 font-mono text-center font-bold focus:bg-white focus:outline-none focus:border-amber-600 shadow-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SEÇÃO 2: DADOS DO CONTADOR / SEFAZ (CSC E IdCSC) */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-800 border-b pb-1.5">
                  <Key className="w-4 h-4 text-zinc-500" /> 2. Chaves do Contador / Posto Fiscal SEFAZ-SP
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="font-bold text-zinc-700 mb-1 flex items-center gap-1">
                      Identificador (IdCSC):
                      <span className="text-[10px] text-zinc-400 font-normal">(Ex: 000001)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="000001"
                      value={cscTokenId}
                      onChange={e => setCscTokenId(e.target.value)}
                      required
                      className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 text-zinc-900 font-mono text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block font-bold text-zinc-700 mb-1">
                      Token CSC (Código de Segurança do Contribuinte):
                    </label>
                    <div className="relative">
                      <input
                        type={showCscValue ? 'text' : 'password'}
                        placeholder="Cole o código alfanumérico fornecido pelo contador"
                        value={cscTokenValue}
                        onChange={e => setCscTokenValue(e.target.value)}
                        required={!currentSettings?.id}
                        className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 pr-10 text-zinc-900 font-mono text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCscValue(!showCscValue)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
                      >
                        {showCscValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* SEÇÃO 3: CERTIFICADO DIGITAL A1 */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-800 border-b pb-1.5">
                  <ShieldCheck className="w-4 h-4 text-zinc-500" /> 3. Certificado Digital e-CNPJ A1 (.pfx)
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">
                      Arquivo do Certificado (.pfx ou .p12):
                    </label>
                    <label className="border-2 border-dashed border-zinc-300 hover:border-amber-500 rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 cursor-pointer bg-zinc-50 hover:bg-amber-50/20 transition-all text-center">
                      <Upload className="w-5 h-5 text-zinc-400" />
                      <span className="text-[11px] font-bold text-zinc-700 truncate max-w-50">
                        {pfxFileName || (currentSettings?.certificate_valid_until ? 'Substituir Certificado Atual' : 'Selecionar arquivo .pfx')}
                      </span>
                      <span className="text-[10px] text-zinc-400">Padrão ICP-Brasil A1</span>
                      <input
                        type="file"
                        accept=".pfx,.p12"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div>
                    <label className="block font-bold text-zinc-700 mb-1">
                      Senha do Certificado Digital:
                    </label>
                    <div className="relative">
                      <input
                        type={showCertPassword ? 'text' : 'password'}
                        placeholder={currentSettings?.certificate_valid_until ? '•••••••• (Preencha se trocar o arquivo)' : 'Senha do arquivo .pfx'}
                        value={certificatePassword}
                        onChange={e => setCertificatePassword(e.target.value)}
                        required={!currentSettings?.certificate_valid_until}
                        className="w-full bg-zinc-50 border border-zinc-300 rounded-lg p-2.5 pr-10 text-zinc-900 text-xs focus:bg-white focus:outline-none focus:border-amber-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCertPassword(!showCertPassword)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
                      >
                        {showCertPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Seletor de Ambiente SEFAZ */}
                    <div className="mt-3">
                      <label className="block font-bold text-zinc-700 mb-1">Ambiente de Emissão:</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setAmbiente(2)}
                          className={`p-2 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                            ambiente === 2 
                              ? 'bg-amber-50 border-amber-500 text-amber-900 ring-1 ring-amber-400' 
                              : 'bg-zinc-50 border-zinc-200 text-zinc-600'
                          }`}
                        >
                          🧪 Homologação (Testes)
                        </button>
                        <button
                          type="button"
                          onClick={() => setAmbiente(1)}
                          className={`p-2 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                            ambiente === 1 
                              ? 'bg-emerald-50 border-emerald-500 text-emerald-900 ring-1 ring-emerald-400' 
                              : 'bg-zinc-50 border-zinc-200 text-zinc-600'
                          }`}
                        >
                          🏛️ Produção (Oficial)
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SEÇÃO 4: MÓDULOS DE EMISSÃO & SÉRIES FISCAIS (TRÍADE FISCAL) */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-800 border-b pb-1.5">
                  <FileCheck2 className="w-4 h-4 text-zinc-500" /> 4. Módulos de Emissão & Séries Fiscais (Tríade Fiscal)
                </div>

                <div className="space-y-2.5 text-xs">
                  {/* Card NFC-e (Mod. 65) */}
                  <div className={`p-3 rounded-xl border transition-all ${
                    nfceEnabled ? 'bg-amber-50/40 border-amber-300' : 'bg-zinc-50 border-zinc-200'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="enableNfce"
                          checked={nfceEnabled}
                          onChange={e => setNfceEnabled(e.target.checked)}
                          className="w-4 h-4 text-amber-600 rounded border-zinc-300 focus:ring-amber-500 cursor-pointer"
                        />
                        <label htmlFor="enableNfce" className="cursor-pointer">
                          <span className="font-bold text-zinc-900 block">NFC-e — Varejo e Consumidor Final (Mod. 65)</span>
                          <span className="text-[11px] text-zinc-500 block">Frente de caixa PDV, sorveterias, varejo e balcão rápido com QR Code.</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        <label className="text-[11px] font-bold text-zinc-600">Série:</label>
                        <input
                          type="number"
                          min={1}
                          max={999}
                          value={seriePadraoNfce}
                          onChange={e => setSeriePadraoNfce(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 bg-white border border-zinc-300 rounded-lg p-1.5 text-center font-mono font-bold text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card NF-e (Mod. 55) */}
                  <div className={`p-3 rounded-xl border transition-all ${
                    nfeModel55Enabled ? 'bg-indigo-50/40 border-indigo-300' : 'bg-zinc-50 border-zinc-200'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="enableNfe55"
                          checked={nfeModel55Enabled}
                          onChange={e => setNfeModel55Enabled(e.target.checked)}
                          className="w-4 h-4 text-indigo-600 rounded border-zinc-300 focus:ring-indigo-500 cursor-pointer"
                        />
                        <label htmlFor="enableNfe55" className="cursor-pointer">
                          <span className="font-bold text-zinc-900 flex items-center gap-2">
                            NF-e — Mercadorias & B2B (Mod. 55)
                            <span className="text-[9px] font-black uppercase bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">Plano Empresa</span>
                          </span>
                          <span className="text-[11px] text-zinc-500 block">Venda empresarial, faturamento com frete, duplicatas e DANFE A4 retrato/paisagem.</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        <label className="text-[11px] font-bold text-zinc-600">Série:</label>
                        <input
                          type="number"
                          min={1}
                          max={999}
                          value={seriePadraoNfe55}
                          onChange={e => setSeriePadraoNfe55(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 bg-white border border-zinc-300 rounded-lg p-1.5 text-center font-mono font-bold text-xs"
                        />
                      </div>
                    </div>

                    {/* Opções Avançadas do DANFE NF-e 55: Layout (Retrato vs Paisagem) e Logotipo */}
                    {nfeModel55Enabled && (
                      <div className="mt-3 pt-3 border-t border-indigo-100/80 space-y-3">
                        {/* Seletor de Orientação da Folha A4 */}
                        <div>
                          <label className="text-[11px] font-bold text-zinc-700 flex items-center justify-between mb-1.5">
                            <span className="flex items-center gap-1.5">
                              <FileText className="w-3.5 h-3.5 text-indigo-600" />
                              Formato de Impressão do DANFE A4:
                            </span>
                            <span className="text-[10px] text-zinc-400 font-normal">
                              Padrão SEFAZ MOC 7.0
                            </span>
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setDanfeLayoutNfe55('retrato')}
                              className={`flex items-start gap-2.5 p-2 rounded-lg border text-left transition-all ${
                                danfeLayoutNfe55 === 'retrato'
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                  : 'bg-white text-zinc-700 border-zinc-200 hover:border-indigo-300 hover:bg-indigo-50/20'
                              }`}
                            >
                              <div className={`w-4 h-5 mt-0.5 rounded-xs border-2 shrink-0 flex flex-col justify-between p-0.5 ${
                                danfeLayoutNfe55 === 'retrato' ? 'border-white bg-white/20' : 'border-zinc-400 bg-zinc-100'
                              }`}>
                                <div className={`h-0.5 w-full rounded-xs ${danfeLayoutNfe55 === 'retrato' ? 'bg-white' : 'bg-zinc-400'}`} />
                                <div className={`h-0.5 w-2/3 rounded-xs ${danfeLayoutNfe55 === 'retrato' ? 'bg-white' : 'bg-zinc-400'}`} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-bold leading-tight flex items-center gap-1">
                                  Retrato (Vertical)
                                  {danfeLayoutNfe55 === 'retrato' && <Check className="w-3 h-3 text-white shrink-0" />}
                                </div>
                                <div className={`text-[10px] leading-snug mt-0.5 ${danfeLayoutNfe55 === 'retrato' ? 'text-indigo-100' : 'text-zinc-500'}`}>
                                  Padrão clássico A4 (MOC 3.8.1). Ideal para envio por e-mail e impressões verticais.
                                </div>
                              </div>
                            </button>

                            <button
                              type="button"
                              onClick={() => setDanfeLayoutNfe55('paisagem')}
                              className={`flex items-start gap-2.5 p-2 rounded-lg border text-left transition-all ${
                                danfeLayoutNfe55 === 'paisagem'
                                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                  : 'bg-white text-zinc-700 border-zinc-200 hover:border-indigo-300 hover:bg-indigo-50/20'
                              }`}
                            >
                              <div className={`w-5 h-4 mt-0.5 rounded-xs border-2 shrink-0 flex flex-col justify-between p-0.5 ${
                                danfeLayoutNfe55 === 'paisagem' ? 'border-white bg-white/20' : 'border-zinc-400 bg-zinc-100'
                              }`}>
                                <div className={`h-0.5 w-full rounded-xs ${danfeLayoutNfe55 === 'paisagem' ? 'bg-white' : 'bg-zinc-400'}`} />
                                <div className={`h-0.5 w-2/3 rounded-xs ${danfeLayoutNfe55 === 'paisagem' ? 'bg-white' : 'bg-zinc-400'}`} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-bold leading-tight flex items-center gap-1">
                                  Paisagem (Horizontal)
                                  {danfeLayoutNfe55 === 'paisagem' && <Check className="w-3 h-3 text-white shrink-0" />}
                                </div>
                                <div className={`text-[10px] leading-snug mt-0.5 ${danfeLayoutNfe55 === 'paisagem' ? 'text-indigo-100' : 'text-zinc-500'}`}>
                                  Padrão expandido (MOC 3.8.2). Ideal para notas com muitos itens e colunas fiscais.
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>

                        {/* Espaço do Logotipo da Empresa no DANFE A4 */}
                        <div className="bg-white/80 p-2.5 rounded-lg border border-indigo-100 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <label className="text-[11px] font-bold text-zinc-800 flex items-center gap-1.5">
                                <ImageIcon className="w-3.5 h-3.5 text-indigo-600" />
                                Logotipo da Empresa no DANFE A4
                              </label>
                              <span className="text-[10px] text-zinc-500 block leading-tight mt-0.5">
                                O logotipo será impresso no quadro superior esquerdo do DANFE Modelo 55 (DocBuilder nativo).
                              </span>
                            </div>
                            {logoUrl && (
                              <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                                Ativo no DANFE
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="url"
                              value={logoUrl}
                              onChange={e => setLogoUrl(e.target.value)}
                              placeholder="https://exemplo.com/logotipo.png ou deixe em branco para usar o cadastrado"
                              className="flex-1 bg-white border border-zinc-300 rounded-lg p-1.5 text-xs text-zinc-800 placeholder-zinc-400 focus:ring-1 focus:ring-indigo-500"
                            />
                            {defaultLogoUrl && defaultLogoUrl !== logoUrl && (
                              <button
                                type="button"
                                onClick={() => setLogoUrl(defaultLogoUrl)}
                                className="shrink-0 px-2 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-[10px] font-bold rounded-lg border border-zinc-300 transition-colors"
                                title="Usar logotipo cadastrado no perfil da empresa"
                              >
                                Usar da Empresa
                              </button>
                            )}
                            {logoUrl && (
                              <button
                                type="button"
                                onClick={() => setLogoUrl('')}
                                className="shrink-0 p-1.5 text-zinc-400 hover:text-red-500 transition-colors"
                                title="Remover logotipo do DANFE"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Preview do Logotipo */}
                          {logoUrl && (
                            <div className="flex items-center gap-3 p-2 bg-zinc-50 rounded border border-zinc-200">
                              <div className="w-16 h-10 bg-white border border-zinc-200 rounded flex items-center justify-center overflow-hidden p-1 shrink-0">
                                <img
                                  src={logoUrl}
                                  alt="Preview Logotipo DANFE"
                                  className="max-w-full max-h-full object-contain"
                                  onError={e => {
                                    (e.target as HTMLElement).style.display = 'none';
                                  }}
                                />
                              </div>
                              <div className="text-[10px] text-zinc-600 leading-tight">
                                <span className="font-bold text-zinc-800 block">Pré-visualização do Emissor:</span>
                                A imagem será ajustada proporcionalmente no quadro superior sem distorção.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card NFS-e (Nacional ADN) */}
                  <div className={`p-3 rounded-xl border transition-all ${
                    nfseEnabled ? 'bg-emerald-50/40 border-emerald-300' : 'bg-zinc-50 border-zinc-200'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="enableNfse"
                          checked={nfseEnabled}
                          onChange={e => setNfseEnabled(e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded border-zinc-300 focus:ring-emerald-500 cursor-pointer"
                        />
                        <label htmlFor="enableNfse" className="cursor-pointer">
                          <span className="font-bold text-zinc-900 flex items-center gap-2">
                            NFS-e — Nota de Serviço Nacional (Padrão ADN)
                            <span className="text-[9px] font-black uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Especialista+</span>
                          </span>
                          <span className="text-[11px] text-zinc-500 block">Ordens de serviço, assistência técnica, clínicas e prestadores (Receita Federal).</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        <label className="text-[11px] font-bold text-zinc-600">Série:</label>
                        <input
                          type="number"
                          min={1}
                          max={999}
                          value={seriePadraoNfse}
                          onChange={e => setSeriePadraoNfse(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 bg-white border border-zinc-300 rounded-lg p-1.5 text-center font-mono font-bold text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões de Ação do Rodapé */}
              <div className="pt-3 border-t border-zinc-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-700 text-xs font-bold hover:bg-zinc-100 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs font-black tracking-wide shadow-md hover:shadow-lg transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Validando e Criptografando...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 stroke-3" />
                      <span>Salvar e Habilitar Módulo Fiscal</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-zinc-200 pb-3 flex-wrap gap-2">
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-zinc-900 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-zinc-600" />
                    Notas Fiscais Emitidas na SEFAZ ({emittedDocuments.length})
                  </h4>
                  <p className="text-[11px] text-zinc-500">
                    Registro oficial das notas fiscais emitidas pelo PDV e salvas no banco de dados
                  </p>
                </div>
                <button
                  type="button"
                  onClick={loadEmittedDocuments}
                  disabled={loadingDocs}
                  className="px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingDocs ? 'animate-spin' : ''}`} />
                  <span>Atualizar</span>
                </button>
              </div>

              {loadingDocs ? (
                <div className="py-12 flex flex-col items-center justify-center gap-2 text-zinc-400 text-xs">
                  <RefreshCw className="w-5 h-5 animate-spin text-amber-500" />
                  <span>Carregando notas fiscais do banco...</span>
                </div>
              ) : emittedDocuments.length === 0 ? (
                <div className="py-12 text-center text-zinc-400 text-xs bg-zinc-50 rounded-xl border border-zinc-200 p-6 space-y-1.5">
                  <div className="w-10 h-10 rounded-full bg-zinc-100 text-zinc-400 flex items-center justify-center mx-auto">
                    <FileText className="w-5 h-5" />
                  </div>
                  <p className="font-bold text-zinc-700">Nenhuma nota fiscal registrada ainda.</p>
                  <p className="text-[11px] text-zinc-500 max-w-md mx-auto">
                    As notas emitidas pelo caixa ou geradas a partir de comprovantes de vendas anteriores aparecerão aqui com status oficial, chave de acesso de 44 dígitos e botões para download do XML e DANFE.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Feedback de Mensagens na Aba de Documentos */}
                  {errorMsg && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2.5 text-red-800 text-xs">
                      <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                      <span>{errorMsg}</span>
                    </div>
                  )}
                  {successMsg && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-emerald-800 text-xs">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>{successMsg}</span>
                    </div>
                  )}

                  {emittedDocuments.some((d: any) => String(d.cstat) === '245' && d.status === 'rejected') && (
                    <div className="p-3.5 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-950 space-y-1.5">
                      <div className="font-black uppercase text-[11px] text-amber-900 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        <span>Diagnóstico SEFAZ — Rejeição 245 (CNPJ Emitente não cadastrado no ambiente selecionado)</span>
                      </div>
                      <p className="text-[11px] text-amber-900 leading-relaxed">
                        O XML e a assinatura digital A1 foram aceitos pela SEFAZ-SP, mas o CNPJ não consta habilitado no ambiente de <strong>Homologação (Testes)</strong> ou falta o credenciamento específico da NFC-e:
                      </p>
                      <ul className="list-disc pl-4 space-y-1 text-[11px] text-amber-900">
                        <li>
                          <strong>Se o contador credenciou a empresa e gerou o CSC direto em Produção:</strong> acesse a aba <strong>Configurações &amp; Certificado A1</strong>, mude o campo <strong>Ambiente da SEFAZ</strong> para <strong>Produção (Validade Jurídica)</strong> e clique em Salvar.
                        </li>
                        <li>
                          <strong>Se a empresa ainda não foi credenciada na NFC-e SP:</strong> o contador ou responsável deve acessar o portal <code>nfce.fazenda.sp.gov.br</code> (menu <em>Credenciamento</em>) e habilitar o estabelecimento para emissão de NFC-e (Modelo 65).
                        </li>
                      </ul>
                    </div>
                  )}

                  {emittedDocuments.some((d: any) => String(d.cstat) === '391' && d.status === 'rejected') && (
                    <div className="p-3.5 bg-blue-50 border border-blue-300 rounded-xl text-xs text-blue-950 space-y-1.5">
                      <div className="font-black uppercase text-[11px] text-blue-900 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-blue-600 shrink-0" />
                        <span>Diagnóstico SEFAZ — Rejeição 391 (Identificação de Pagamento Eletrônico / Cartão / PIX)</span>
                      </div>
                      <p className="text-[11px] text-blue-900 leading-relaxed">
                        A SEFAZ exige a indicação do tipo de integração nas formas de pagamento eletrônicas (Cartão de Crédito, Cartão de Débito ou PIX). Como o balcão opera com maquininha física (POS) não integrada via TEF:
                      </p>
                      <ul className="list-disc pl-4 space-y-1 text-[11px] text-blue-900">
                        <li>
                          <strong>Solução Automática Aplicada:</strong> O gerador de XML do R-MAX foi ajustado para estruturar a tag <code>&lt;card&gt;</code> com <code>tpIntegra = 2</code> (Operação não integrada com o POS), em conformidade estrita com a NT 2020.006 da SEFAZ.
                        </li>
                        <li>
                          <strong>Novas Vendas:</strong> As próximas emissões no PDV para Cartão e PIX já serão enviadas com a tag regularizada.
                        </li>
                      </ul>
                    </div>
                  )}

                  {emittedDocuments.some(
                    (d: any) =>
                      String(d.cstat) === '252' &&
                      d.status === 'rejected' &&
                      !d.motivo_status?.includes('[Regularizada')
                  ) && (
                    <div className="p-3.5 bg-rose-50 border border-rose-300 rounded-xl text-xs text-rose-950 space-y-1.5">
                      <div className="font-black uppercase text-[11px] text-rose-900 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                        <span>Diagnóstico SEFAZ — Rejeição 252 (Ambiente de Envio Divergente)</span>
                      </div>
                      <p className="text-[11px] text-rose-900 leading-relaxed">
                        A nota declarou emissão oficial em <strong>Produção</strong>, mas a conexão SOAP foi enviada ao canal de <strong>Homologação (Testes)</strong> devido à transição de ambiente do caixa:
                      </p>
                      <ul className="list-disc pl-4 space-y-1 text-[11px] text-rose-900">
                        <li>
                          <strong>Nenhum imposto gerado:</strong> A SEFAZ rejeitou o documento na entrada. Não há débitos tributários nem duplicidade de venda.
                        </li>
                        <li>
                          <strong>Como regularizar:</strong> O canal de Produção está ativo. Basta clicar no botão <strong>⚡ Reemitir com Mesmo Nº</strong> no cartão da nota abaixo para autorizar o documento oficial mantendo a numeração sequencial.
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* Banner Regulatório CONFAZ: Ajustes SINIEF 40/2026 e 38/2026 */}
                  <div className="p-3.5 bg-emerald-50/80 border border-emerald-300/80 rounded-xl text-xs text-emerald-950 space-y-1.5">
                    <div className="font-bold text-[11px] text-emerald-900 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                        <span>Conformidade CONFAZ — Ajustes SINIEF nº 40/2026 (NF-e 55) e nº 38/2026 (NFC-e 65)</span>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-200/60 text-emerald-900 font-mono text-[10px] font-bold rounded">
                        Vigência: 01/11/2026
                      </span>
                    </div>
                    <p className="text-[11px] text-emerald-900 leading-relaxed">
                      Para emissores do Estado de <strong>São Paulo (SEFAZ-SP)</strong>, Bahia e Acre, o procedimento de <em>"Pedido de Inutilização de Número"</em> foi oficialmente extinto. Em caso de nota rejeitada, o R-MAX retransmite utilizando o <strong>mesmo número fiscal</strong>, preservando a sequência e evitando saltos no livro fiscal da sua empresa.
                    </p>
                  </div>

                  {emittedDocuments.map((doc: any) => (
                    <div
                      key={doc.id}
                      className="p-3.5 bg-zinc-50 hover:bg-zinc-100/70 border border-zinc-200 rounded-xl text-xs space-y-2.5 transition-colors"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-zinc-900 text-sm">
                            {Number(doc.modelo) === 65 ? 'NFC-e (Mod 65)' : Number(doc.modelo) === 55 ? 'NF-e (Mod 55)' : 'NFS-e Nacional'} Nº {doc.numero} (Série {doc.serie})
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                            doc.status === 'authorized'
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : doc.status === 'rejected'
                              ? 'bg-rose-100 text-rose-800 border border-rose-300'
                              : 'bg-amber-100 text-amber-800 border border-amber-300'
                          }`}>
                            {doc.status === 'authorized'
                              ? `Autorizada (${doc.cstat || '100'})`
                              : doc.status === 'rejected'
                              ? `Rejeitada (${doc.cstat})`
                              : 'Contingência'}
                          </span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            Number(doc.ambiente) === 1 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {Number(doc.ambiente) === 1 ? 'Produção' : 'Homologação'}
                          </span>
                        </div>

                        <div className="font-mono font-black text-sm text-emerald-600">
                          {formatCurrencyBRL(doc.valor_total || 0)}
                        </div>
                      </div>

                      {doc.chave_acesso && (
                        <div className="p-2 bg-white rounded-lg border border-zinc-200 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold uppercase text-zinc-400 block">Chave de Acesso:</span>
                            <span className="font-mono text-[11px] text-zinc-700 font-semibold break-all">
                              {doc.chave_acesso}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyKey(doc.chave_acesso)}
                            className="p-1.5 hover:bg-zinc-100 rounded text-zinc-500 hover:text-zinc-800 shrink-0 cursor-pointer transition-colors"
                            title="Copiar Chave de Acesso"
                          >
                            {copiedKey === doc.chave_acesso ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600 stroke-3" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      )}

                      {doc.status === 'rejected' && (() => {
                        const diag = getSefazDiagnosis(doc.cstat, doc.motivo_status);
                        return (
                          <div className="p-3 bg-rose-50/80 border border-rose-200 rounded-xl space-y-2 text-xs">
                            <div className="flex items-center gap-2 font-bold text-rose-900 text-[11px]">
                              <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                              <span>{diag.title}</span>
                            </div>
                            <div className="space-y-1 text-[11px] text-zinc-700 leading-relaxed">
                              <div>
                                <strong className="text-zinc-900 font-semibold">O que aconteceu: </strong>
                                <span>{diag.whatHappened}</span>
                              </div>
                              <div>
                                <strong className="text-zinc-900 font-semibold">Impacto fiscal: </strong>
                                <span className="text-emerald-800 font-medium">{diag.taxImpact}</span>
                              </div>
                              <div>
                                <strong className="text-zinc-900 font-semibold">Como resolver: </strong>
                                <span>{diag.solution}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex items-center justify-between text-[11px] text-zinc-500 pt-1 flex-wrap gap-2 border-t border-zinc-200/50">
                        <div>
                          <span>Emissão: {doc.data_emissao ? new Date(doc.data_emissao).toLocaleString('pt-BR') : '-'}</span>
                          {doc.motivo_status && (
                            <span className="ml-2 text-zinc-600 italic">({doc.motivo_status})</span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {doc.status === 'rejected' && (
                            doc.motivo_status?.includes('[Regularizada') ? (
                              <span className="px-2.5 py-1 bg-emerald-50 border border-emerald-300 text-emerald-800 font-bold text-[10px] rounded-lg flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                <span>Regularizada</span>
                              </span>
                            ) : (
                              <button
                                type="button"
                                disabled={retryingDocId === doc.id}
                                onClick={() => handleRetryDocument(doc.id)}
                                className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold text-[10px] rounded-lg flex items-center gap-1 cursor-pointer transition-colors shadow-2xs disabled:opacity-50"
                                title="Reemitir esta venda na SEFAZ com o canal e credenciais ativas de produção"
                              >
                                {retryingDocId === doc.id ? (
                                  <>
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    <span>Reemitindo...</span>
                                  </>
                                ) : (
                                  <>
                                    <Zap className="w-3 h-3 fill-current" />
                                    <span>Reemitir com Mesmo Nº</span>
                                  </>
                                )}
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            disabled={downloadingDocId === doc.id}
                            onClick={() => handleDownloadXml(doc.id, `nfce_${doc.chave_acesso || doc.numero}.xml`)}
                            className="px-2.5 py-1 bg-white hover:bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-lg font-bold text-[10px] flex items-center gap-1 cursor-pointer transition-colors shadow-2xs"
                            title={doc.status === 'rejected' ? 'Baixar arquivo XML enviado na tentativa' : 'Baixar arquivo XML protocolado'}
                          >
                            <Download className="w-3 h-3 text-zinc-500" />
                            <span>{doc.status === 'rejected' ? 'Baixar XML Enviado' : 'Baixar XML'}</span>
                          </button>
                          {doc.status !== 'rejected' && (
                            <button
                              type="button"
                              disabled={downloadingDocId === doc.id}
                              onClick={() => handleDownloadPdf(doc.id, `danfe_${doc.chave_acesso || doc.numero}.pdf`)}
                              className="px-2.5 py-1 bg-white hover:bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-lg font-bold text-[10px] flex items-center gap-1 cursor-pointer transition-colors shadow-2xs"
                              title="Baixar Documento Auxiliar em PDF"
                            >
                              <Printer className="w-3 h-3 text-zinc-500" />
                              <span>Baixar DANFE (PDF)</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FiscalConfigModal;
